import { FC } from 'react';
import { Box, Skeleton, Svg, Text, Timeline, Tooltip } from '@hopskipdrive/react-components';
import { CheckRounded as CheckRoundedIcon, DescriptionOutlined as DescriptionOutlinedIcon } from '@mui/icons-material';
import pluralize from 'pluralize';

import { CopyToClipboard } from '../../../../../shared/components/CopyToClipboard';
import { CheckeredFlagIcon, LocationPinIcon } from '../../../../../shared/components/Icons';
import { PhotoOrInitials } from '../../../../../shared/components/PhotoOrInitials';
import { colorTokens } from '../../../../../shared/components/variables';
import { useFlags } from '../../../../../shared/contexts/LDContext';
import { formatInTimezone } from '../../../../../shared/utils/helpers/date';
import { getFullNameFirstLettersUpper } from '../../../../../shared/utils/helpers/getName';
import { OutlinedCircle } from '../../../../components/OutlineCircleNumber';
import { RideOverviewDetails, Rider, RouteDetail } from '../../../../types/rideOverview';
import { LocationInnerCard } from '../LocationInnerCard';
import { TimeDifferenceTag } from '../TimeDifferenceTag';

import {
  DashedConnector,
  FilledCircle,
  InnerCard,
  SolidConnector,
  TimelineContainer,
  TimestampContainer,
} from './TimelineOverview.styles';

interface TimelineOverviewProps {
  isFetching: boolean;
  rideOverviewDetails: RideOverviewDetails;
}

export const TimelineOverview: FC<TimelineOverviewProps> = ({ isFetching, rideOverviewDetails }) => {
  const routeDetails = rideOverviewDetails?.routeDetails;
  const hasCheckedIn = !!rideOverviewDetails?.checkInDetails?.actualCheckedInAt;

  if (isFetching) {
    return <TimelineOverviewSkeleton />;
  }

  return (
    <TimelineContainer>
      <Timeline>
        <CheckInTimelineItem
          timeZone={rideOverviewDetails?.timeZone}
          checkInDetails={rideOverviewDetails?.checkInDetails}
          actualStartsAt={rideOverviewDetails.actualStartsAt}
        />
        {hasCheckedIn && (
          <LeaveByTimelineItem
            timeZone={rideOverviewDetails?.timeZone}
            leaveAt={rideOverviewDetails?.leaveAt}
            leaveAtDetectedLocationDetails={rideOverviewDetails?.leaveAtDetectedLocationDetails}
            actualStartsAt={rideOverviewDetails?.actualStartsAt}
            routeDetails={routeDetails}
          />
        )}
        {routeDetails?.map((routeDetail, index) => (
          <RouteDetailTimelineItem
            key={`timeline-route-detail-${index}`}
            index={index}
            timeZone={rideOverviewDetails?.timeZone}
            timeAnchor={rideOverviewDetails?.timeAnchor}
            actualEndsAt={rideOverviewDetails?.actualEndsAt}
            routeDetail={routeDetail}
            previousRouteDetail={index ? routeDetails[index - 1] : undefined}
            etaToFirstWaypoint={rideOverviewDetails?.etaToFirstWaypoint}
            nextRouteDetail={routeDetails[index + 1]}
            routeDetailsLength={routeDetails?.length}
            hasCheckedIn={hasCheckedIn}
          />
        ))}
        <Timeline.Item
          dotContent={
            <FilledCircle $isCompleted={!!rideOverviewDetails.actualEndsAt}>
              <CheckeredFlagIcon />
            </FilledCircle>
          }
        >
          <ScheduledTimestampTooltip
            plannedTime={rideOverviewDetails.endsAt}
            disabled={!rideOverviewDetails.actualEndsAt}
            timeZone={rideOverviewDetails?.timeZone}
            prefix=" completion"
          >
            {rideOverviewDetails.actualEndsAt ? (
              <Box display="flex" flexDirection="row" alignItems="center">
                <Text variant="h6" style={{ color: colorTokens.text.primary, lineHeight: '130%' }}>
                  {`Ride complete: ${formatInTimezone(
                    rideOverviewDetails.actualEndsAt,
                    rideOverviewDetails?.timeZone,
                    'h:mm aa zzz'
                  )}`}
                </Text>
                <TimeDifferenceTag
                  timeA={rideOverviewDetails.actualEndsAt || null}
                  timeB={rideOverviewDetails.endsAt || null}
                />
              </Box>
            ) : (
              <Text variant="h6" style={{ color: colorTokens.text.secondary, lineHeight: '130%' }}>
                {'Ride Complete'}
              </Text>
            )}
          </ScheduledTimestampTooltip>
        </Timeline.Item>
      </Timeline>
    </TimelineContainer>
  );
};

const PICKUP = 'pick_up';
const DROPOFF = 'drop_off';

export const helpers = {
  [PICKUP]: {
    getWaypointTitle: (index) => (index ? 'drop-off' : 'pickup'),
    getWaypointType: (index) => (index ? DROPOFF : PICKUP),
  },
  [DROPOFF]: {
    getWaypointTitle: (index, waypointCount) => (index + 1 === waypointCount ? 'drop-off' : 'pickup'),
    getWaypointType: (index, waypointCount) => (index + 1 === waypointCount ? DROPOFF : PICKUP),
  },
};

const TimelineOverviewSkeleton: FC = () => {
  return (
    <TimelineContainer>
      <Timeline>
        <Timeline.Item dotContent={<OutlinedCircle />} connector={<DashedConnector />}>
          <Skeleton data-testid="timeline-overview-skeleton" variant="text" width={250} height={32} />
        </Timeline.Item>
        <Timeline.Item dotContent={<OutlinedCircle>1</OutlinedCircle>} connector={<DashedConnector />}>
          <Box display="flex" flexDirection="row" alignItems="center" gap="4px">
            <Skeleton data-testid="timeline-overview-skeleton" variant="text" width={270} height={32} />
          </Box>
          <Skeleton variant="text" width={150} height={24} />
          <RidersList isFetching={true} riders={[]} />
          <InnerCard>
            <Box display="flex" flexDirection="row" alignItems="center">
              <LocationPinIcon />
              <Skeleton variant="text" width={250} height={32} />
            </Box>
            <Box gap="4px" ml="20px">
              <Skeleton variant="text" width={200} height={24} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>

            <Box display="flex" flexDirection="row" alignItems="start">
              <DescriptionOutlinedIcon />
              <Skeleton variant="text" width={200} height={24} />
            </Box>
          </InnerCard>
        </Timeline.Item>
        <Timeline.Item dotContent={<OutlinedCircle>2</OutlinedCircle>} connector={<DashedConnector />}>
          <Box display="flex" flexDirection="row" alignItems="center" gap="4px">
            <Skeleton data-testid="timeline-overview-skeleton" variant="text" width={270} height={32} />
          </Box>
          <Skeleton variant="text" width={150} height={24} />
          <RidersList isFetching={true} riders={[]} />
          <InnerCard>
            <Box display="flex" flexDirection="row" alignItems="center">
              <LocationPinIcon />
              <Skeleton variant="text" width={250} height={32} />
            </Box>
            <Box gap="4px" ml="20px">
              <Skeleton variant="text" width={200} height={24} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
            <Box display="flex" flexDirection="row" alignItems="start">
              <DescriptionOutlinedIcon />
              <Skeleton variant="text" width={200} height={24} />
            </Box>
          </InnerCard>
        </Timeline.Item>
        <Timeline.Item dotContent={<OutlinedCircle />}>
          <Skeleton data-testid="timeline-overview-skeleton" variant="text" width={200} height={24} />
        </Timeline.Item>
      </Timeline>
    </TimelineContainer>
  );
};

const CheckInTimelineItem: FC<{
  checkInDetails: RideOverviewDetails['checkInDetails'];
  actualStartsAt: RideOverviewDetails['actualStartsAt'];
  timeZone: string;
}> = ({ actualStartsAt, checkInDetails, timeZone }) => {
  const hasCheckedIn = checkInDetails?.actualCheckedInAt;

  return (
    <Timeline.Item
      dotContent={
        checkInDetails?.actualCheckedInAt ? (
          <FilledCircle $isCompleted>
            <CheckRoundedIcon
              style={{
                width: '20px',
                height: '20px',
              }}
            />
          </FilledCircle>
        ) : (
          <OutlinedCircle />
        )
      }
      connector={
        hasCheckedIn && !actualStartsAt ? <DashedConnector /> : <SolidConnector $isCompleted={!!actualStartsAt} />
      }
    >
      {hasCheckedIn ? (
        <>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Text
              variant="h6"
              style={{ color: colorTokens.text.primary, lineHeight: '130%' }}
            >{`Checked in at: ${formatInTimezone(
              checkInDetails?.actualCheckedInAt || '',
              timeZone,
              'h:mm aa zzz'
            )}`}</Text>
            <TimeDifferenceTag
              timeA={checkInDetails?.actualCheckedInAt || null}
              timeB={checkInDetails?.plannedEndTime || null}
            />
          </Box>

          <Text
            variant="body1"
            fontWeight={300}
            style={{ color: colorTokens.text.secondary }}
          >{`Scheduled check in: ${formatInTimezone(
            checkInDetails.plannedStartTime,
            timeZone,
            'h:mm aa'
          )} - ${formatInTimezone(checkInDetails.plannedEndTime, timeZone, 'h:mm aa zzz')} Client Time`}</Text>
        </>
      ) : (
        <Text variant="h6" style={{ color: colorTokens.text.secondary }}>{`Check in: ${formatInTimezone(
          checkInDetails.plannedStartTime,
          timeZone,
          'h:mm aa'
        )} - ${formatInTimezone(checkInDetails.plannedEndTime, timeZone, 'h:mm aa')}`}</Text>
      )}
    </Timeline.Item>
  );
};

const LeaveByTimelineItem: FC<{
  leaveAt: RideOverviewDetails['leaveAt'];
  actualStartsAt: RideOverviewDetails['actualStartsAt'];
  leaveAtDetectedLocationDetails: RideOverviewDetails['leaveAtDetectedLocationDetails'];
  routeDetails: RideOverviewDetails['routeDetails'];
  timeZone: string;
}> = ({ leaveAt, actualStartsAt, routeDetails, leaveAtDetectedLocationDetails, timeZone }) => {
  const detectedLatLng = `${leaveAtDetectedLocationDetails?.coordinates?.lat || 0}, ${
    leaveAtDetectedLocationDetails?.coordinates?.lng || 0
  }`;

  return (
    <Timeline.Item
      dotContent={
        actualStartsAt ? (
          <FilledCircle $isCompleted>
            <CheckRoundedIcon
              style={{
                width: '20px',
                height: '20px',
              }}
            />
          </FilledCircle>
        ) : (
          <OutlinedCircle />
        )
      }
      connector={
        !routeDetails[0]?.arrivalDetails?.actual ? (
          <DashedConnector />
        ) : (
          <SolidConnector $isCompleted={!!routeDetails[0]?.arrivalDetails?.actual} />
        )
      }
    >
      {!actualStartsAt ? (
        leaveAt && (
          <Text
            variant="h6"
            style={{ color: colorTokens.text.secondary, lineHeight: '130%' }}
          >{`Leave by: ${formatInTimezone(leaveAt, timeZone, 'h:mm aa')} `}</Text>
        )
      ) : (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Text
            variant="h6"
            style={{ color: colorTokens.text.primary, lineHeight: '130%' }}
          >{`Left at: ${formatInTimezone(actualStartsAt, timeZone, 'h:mm aa zzz')} `}</Text>
          {leaveAt && <TimeDifferenceTag timeA={actualStartsAt} timeB={leaveAt} />}
        </Box>
      )}
      <InnerCard>
        <Box display="flex" flexDirection="row" alignItems="center">
          <LocationPinIcon />
          <Text variant="h6">{'From detected location'}</Text>
        </Box>
        <Box gap="4px" ml="20px">
          <CopyToClipboard
            toastMessage="Address Copied"
            text={detectedLatLng}
            variant="body1"
            textStyle={{ fontWeight: 300 }}
          />
        </Box>
      </InnerCard>

      {leaveAtDetectedLocationDetails?.estimatedDurationToFirstWaypoint && (
        <Text
          variant="h6"
          style={{ color: colorTokens.text.secondary }}
        >{`${leaveAtDetectedLocationDetails?.estimatedDurationToFirstWaypoint}, ${leaveAtDetectedLocationDetails?.estimatedDistanceToFirstWaypoint}`}</Text>
      )}
    </Timeline.Item>
  );
};

const RouteDetailTimelineItem: FC<{
  key: string;
  index: number;
  timeAnchor: string;
  actualEndsAt: RideOverviewDetails['actualEndsAt'];
  etaToFirstWaypoint: RideOverviewDetails['etaToFirstWaypoint'];
  routeDetail: RideOverviewDetails['routeDetails'][number];
  previousRouteDetail?: RideOverviewDetails['routeDetails'][number];
  nextRouteDetail: RideOverviewDetails['routeDetails'][number];
  routeDetailsLength: number;
  hasCheckedIn: boolean;
  timeZone: string;
}> = ({
  key,
  index,
  timeAnchor,
  actualEndsAt,
  etaToFirstWaypoint,
  routeDetail,
  previousRouteDetail,
  nextRouteDetail,
  routeDetailsLength,
  hasCheckedIn,
  timeZone,
}) => {
  const { enableOssCarePartnerRideDetails } = useFlags();

  return (
    <Timeline.Item
      key={key}
      dotContent={
        routeDetail.arrivalDetails.actual ? (
          <FilledCircle $isCompleted>{index + 1}</FilledCircle>
        ) : (
          <OutlinedCircle>{index + 1}</OutlinedCircle>
        )
      }
      connector={
        (hasCheckedIn && !!nextRouteDetail?.arrivalDetails?.actual) ||
        (routeDetailsLength - 1 === index && actualEndsAt) ? (
          <SolidConnector $isCompleted={!!nextRouteDetail?.arrivalDetails?.actual || !!actualEndsAt} />
        ) : (
          <DashedConnector />
        )
      }
    >
      <ScheduledTimestampTooltip
        plannedTime={routeDetail.arrivalDetails.planned}
        disabled={!routeDetail.arrivalDetails.actual}
        timeZone={timeZone}
        prefix={' arrival'}
      >
        {routeDetail.anchor && <Svg icon="anchor-rounded" style={{ color: colorTokens.primary.main }} />}
        <Text
          variant="h6"
          style={{
            color: routeDetail.arrivalDetails.actual ? colorTokens.text.primary : colorTokens.text.secondary,
          }}
        >{`${routeDetail.arrivalDetails.actual ? 'Arrived at' : 'Scheduled'} ${helpers[timeAnchor].getWaypointTitle(
          index,
          routeDetailsLength
        )}: ${formatInTimezone(
          routeDetail.arrivalDetails.actual || routeDetail.arrivalDetails.planned,
          timeZone,
          'h:mm aa zzz'
        )}`}</Text>

        <TimeDifferenceTag
          timeA={routeDetail.arrivalDetails.actual || null}
          timeB={routeDetail.arrivalDetails.planned || null}
        />
      </ScheduledTimestampTooltip>
      <Text
        variant="body1"
        fontWeight={300}
        style={{ color: colorTokens.text.secondary }}
      >{`Procedure time: ${routeDetail.procedureTime}`}</Text>
      {routeDetail.departureDetails?.actual && (
        <ScheduledTimestampTooltip
          plannedTime={routeDetail.departureDetails.planned}
          disabled={!routeDetail.departureDetails.actual}
          timeZone={timeZone}
          prefix={' departure'}
        >
          <Text
            variant="h6"
            style={{
              color: colorTokens.text.primary,
            }}
          >{`Departed from ${helpers[timeAnchor].getWaypointTitle(index, routeDetailsLength)}: ${formatInTimezone(
            routeDetail.departureDetails.actual,
            timeZone,
            'h:mm aa zzz'
          )}`}</Text>
          <TimeDifferenceTag
            timeA={routeDetail.departureDetails.actual || null}
            timeB={routeDetail.departureDetails.planned || null}
          />
        </ScheduledTimestampTooltip>
      )}
      {!routeDetail.arrivalDetails?.actual && (
        <EtaToWaypoint
          waypointIndex={index}
          etaToFirstWaypoint={etaToFirstWaypoint}
          etaToNextWaypoint={previousRouteDetail?.estimatedTimeToNextWaypoint}
          plannedArrival={routeDetail.arrivalDetails.planned}
          timeZone={timeZone}
        />
      )}
      {enableOssCarePartnerRideDetails ? (
        <RidersList
          isFetching={false}
          riders={routeDetail?.riders.filter((rider) => !rider.isRiderAssistant)}
          riderAssistant={routeDetail?.riders.find((rider) => !!rider.isRiderAssistant)}
        />
      ) : (
        <RidersList isFetching={false} riders={routeDetail?.riders} />
      )}
      <LocationInnerCard locationDetails={routeDetail?.locationDetails} />
      {index !== routeDetailsLength - 1 && routeDetail.plannedDuration && (
        <Text
          variant="h6"
          style={{ color: colorTokens.text.secondary }}
        >{`${routeDetail.plannedDuration}, ${routeDetail.distanceToNextWaypoint}`}</Text>
      )}
    </Timeline.Item>
  );
};

interface RidersListProps {
  isFetching: boolean;
  riders: Rider[];
  riderAssistant?: Rider;
}

export const RidersList: FC<RidersListProps> = ({ isFetching, riders, riderAssistant }) => {
  return (
    <>
      {isFetching ? (
        <Box display="flex" flexDirection="row" alignItems="center" minWidth="333px" mt="12px">
          <Skeleton data-testid="timeline-overview-skeleton" variant="circular" width={32} height={32} />
          <Skeleton data-testid="timeline-overview-skeleton" variant="circular" width={32} height={32} />
          <Skeleton data-testid="timeline-overview-skeleton" variant="circular" width={32} height={32} />
          <Skeleton data-testid="timeline-overview-skeleton" variant="text" width={120} />
        </Box>
      ) : (
        <>
          {!!riders.length && (
            <Box display="flex" flexDirection="row" alignItems="center" minWidth="333px" mt="12px">
              {riders?.map((rider) => (
                <PhotoOrInitials
                  pictureUrl={rider?.profilePictureUrl || ''}
                  key={`rider-${rider?.slug}`}
                  fullName={`${rider?.firstName} ${rider?.lastName}`}
                  size={33}
                  fontSize={14}
                />
              ))}

              <Text data-testid="rider-names" variant="h6" ml="8px">
                {`${pluralize('Rider', riders.length)}: `}
                {riders
                  ?.map((rider) => getFullNameFirstLettersUpper(`${rider.firstName} ${rider.lastName}`))
                  .join(', ')}
              </Text>
            </Box>
          )}
          {riderAssistant && (
            <Box display="flex" flexDirection="row" alignItems="center" minWidth="333px" mt="12px">
              <PhotoOrInitials
                pictureUrl={riderAssistant?.profilePictureUrl || ''}
                key={`riderAssistant-${riderAssistant?.slug}`}
                fullName={`${riderAssistant?.firstName} ${riderAssistant?.lastName}`}
                size={33}
                fontSize={14}
              />

              <Text data-testid="riderAssistant-name" variant="h6" ml="8px">
                {'Rider Assistant: '}
                {getFullNameFirstLettersUpper(`${riderAssistant.firstName} ${riderAssistant.lastName}`)}
              </Text>
            </Box>
          )}
        </>
      )}
    </>
  );
};

const EtaToWaypoint: FC<{
  waypointIndex: number;
  etaToFirstWaypoint?: RideOverviewDetails['etaToFirstWaypoint'];
  etaToNextWaypoint?: RouteDetail['estimatedTimeToNextWaypoint'];
  plannedArrival: Date;
  timeZone: string;
}> = ({ waypointIndex, etaToFirstWaypoint, etaToNextWaypoint, plannedArrival, timeZone }) => {
  const driverEstimatedTimeOfArrival = waypointIndex === 0 ? etaToFirstWaypoint?.eta : etaToNextWaypoint;

  if (!driverEstimatedTimeOfArrival) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="4px">
      <Text
        variant="h6"
        style={{
          color: colorTokens.text.primary,
        }}
      >{`ETA: ${formatInTimezone(driverEstimatedTimeOfArrival, timeZone, 'h:mm aa zzz')}`}</Text>
      <TimeDifferenceTag
        prefix="estimated "
        timeA={driverEstimatedTimeOfArrival || null}
        timeB={plannedArrival || null}
      />
    </Box>
  );
};

interface ScheduledTimestampTooltipProps {
  prefix: string;
  plannedTime: Date;
  disabled: boolean;
  timeZone: string;
  children: React.ReactNode;
}

const ScheduledTimestampTooltip: FC<ScheduledTimestampTooltipProps> = ({
  prefix,
  plannedTime,
  disabled,
  timeZone,
  children,
}) => {
  return (
    <Tooltip
      title={
        <CopyToClipboard
          text={`Scheduled${prefix}: ${formatInTimezone(plannedTime, timeZone, 'h:mm aa zzz')} Client Time`}
          toastMessage="Date Copied"
          variant="body1"
        />
      }
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',
          },
        },
      }}
      disableHoverListener={disabled}
    >
      <TimestampContainer>{children}</TimestampContainer>
    </Tooltip>
  );
};
